"use client"
import { Button, Card } from "@/components/atoms"
import { AddressForm, Modal } from "@/components/molecules"
import { emptyDefaultAddressValues } from "@/components/molecules/AddressForm/AddressForm"
import { AddressFormData } from "@/components/molecules/AddressForm/schema"
import { deleteCustomerAddress, setDefaultShippingAddress } from "@/lib/data/customer"
import { cn } from "@/lib/utils"
import { HttpTypes } from "@medusajs/types"
import { useTranslate } from "@tolgee/react"
import { isEmpty } from "lodash"
import { useState, useEffect } from "react"

export const Addresses = ({
  user,
  regions,
  defaultCountryCode,
}: {
  user: HttpTypes.StoreCustomer
  regions: HttpTypes.StoreRegion[]
  /** Prefill country when adding an address in the current storefront region */
  defaultCountryCode?: string
}) => {
  const { t } = useTranslate()
  const [showForm, setShowForm] = useState(false)
  const [deleteAddress, setDeleteAddress] = useState<string | null>(null)
  const [setDefaultAddress, setSetDefaultAddress] = useState<string | null>(null)
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null)

  const [defaultValues, setDefaultValues] = useState<AddressFormData | null>(
    null
  )

  const countries = regions.flatMap((region) => region.countries)

  const handleEdit = (addressId: string) => {
    const address = user.addresses.find((address) => address.id === addressId)
    if (address) {
      setDefaultValues({
        addressId: addressId,
        addressName: address.address_name || "",
        firstName: address.first_name || "",
        lastName: address.last_name || "",
        address: address.address_1 || "",
        city: address.city || "",
        countryCode: address.country_code || "",
        postalCode: address.postal_code || "",
        company: address.company || "",
        province: address.province || "",
        phone: address.phone || user.phone || "",
        isDefaultShipping: address.is_default_shipping || false,
      })
      setShowForm(true)
    }
  }

  const handleDelete = async (addressId: string) => {
    await deleteCustomerAddress(addressId)
    setDeleteAddress(null)
  }

  const handleSetDefault = async (addressId: string) => {
    await setDefaultShippingAddress(addressId)
    setSetDefaultAddress(null)
  }

  const handleAdd = () => {
    setDefaultValues({
      ...emptyDefaultAddressValues,
      countryCode: defaultCountryCode || "",
    })
    setDeleteAddress(null)
    setShowForm(true)
  }

  const toggleMenu = (addressId: string) => {
    setExpandedMenu(expandedMenu === addressId ? null : addressId)
  }

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      // 检查点击的元素是否在菜单内部
      if (expandedMenu && !target.closest('[data-menu-container]')) {
        setExpandedMenu(null)
      }
    }

    if (expandedMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [expandedMenu])


  return (
    <>
      <div
        className={cn(
          "md:col-span-3",
          isEmpty(user.addresses) ? "space-y-8" : "space-y-4"
        )}
      >
        <h1 className="heading-md uppercase">{t("addresses.heading")}</h1>
        {isEmpty(user.addresses) ? (
          <div className="text-center">
            <h3 className="heading-lg text-primary uppercase">
              {t("addresses.no_saved_shipping_addresses")}
            </h3>
            <p className="text-lg text-secondary mt-2">
              {t("addresses.no_saved_shipping_addresses_message")} <br />
              {t("addresses.add_address_prompt")}
            </p>
            <Button onClick={handleAdd} className="mt-4">
              {t("addresses.add_address_button")}
            </Button>
          </div>
        ) : (
          <>
            {user.addresses
              .sort((a, b) => {
                // 默认地址优先
                if (a.is_default_shipping && !b.is_default_shipping) return -1
                if (!a.is_default_shipping && b.is_default_shipping) return 1
                
                // 如果都是或都不是默认地址，按创建时间倒序（最新的在前）
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              })
              .map((address) => (
              <Card
                className="bb-user-panel px-4 pb-8 flex justify-between items-start gap-4 max-w-2xl relative"
                key={address.id}
              >
                <div className="flex flex-col ">
                  <div className="flex items-center gap-2">
                    <h4 className="label-md text-primary">
                      {address.address_name}
                    </h4>
                    {address.is_default_shipping && (
                      <span className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                        {t("addresses.default_shipping")}
                      </span>
                    )}
                  </div>
                  <p className="label-md text-secondary">
                    {`${address.first_name} ${address.last_name}`}
                  </p>
                  {address.company && (
                    <p className="label-md text-secondary">{address.company}</p>
                  )}
                  <p className="label-md text-secondary">
                    {`${address.address_1}, ${address.postal_code} ${
                      address.city
                    }${address.province ? `, ${address.province}` : ""}${`, ${
                      countries.find(
                        (country) =>
                          country && country.iso_2 === address.country_code
                      )?.display_name || address.country_code?.toUpperCase()
                    }`}`}
                  </p>
                  <p className="label-md text-secondary">
                    {`${user.email}, ${address.phone || user.phone}`}
                  </p>
                </div>
                {/* 移动端按钮布局 */}
                <div className="md:hidden relative" data-menu-container>
                  <Button
                    variant="tonal"
                    onClick={() => toggleMenu(address.id)}
                    className="flex items-center gap-1"
                  >
                    More
                    <svg 
                      className={`w-4 h-4 transition-transform ${expandedMenu === address.id ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                  
                  {/* 展开的菜单 */}
                  {expandedMenu === address.id && (
                    <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[200px]">
                      <div className="py-2">
                        <button
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-negative"
                          onClick={() => {
                            setDeleteAddress(address.id)
                            setExpandedMenu(null)
                          }}
                        >
                          {t("addresses.delete_button")}
                        </button>
                        <button
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            handleEdit(address.id)
                            setExpandedMenu(null)
                          }}
                        >
                          {t("addresses.edit_button")}
                        </button>
                        {!address.is_default_shipping && (
                          <button
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                            onClick={() => {
                              setSetDefaultAddress(address.id)
                              setExpandedMenu(null)
                            }}
                          >
                            {t("address_form.set_as_default_shipping")}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* PC端按钮布局 */}
                <div className="hidden md:flex gap-2 sm:gap-4 flex-col-reverse sm:flex-row">
                  <Button
                    variant="tonal"
                    className="text-negative"
                    onClick={() => setDeleteAddress(address.id)}
                  >
                    {t("addresses.delete_button")}
                  </Button>
                  <Button
                    variant="tonal"
                    onClick={() => handleEdit(address.id)}
                  >
                    {t("addresses.edit_button")}
                  </Button>
                </div>
                {/* 右下角的默认地址选择框 - 仅在PC端显示 */}
                <div className="absolute right-4 bottom-2 hidden md:flex items-center space-x-2">
                  {!address.is_default_shipping && (
                    <>
                      <input
                        type="checkbox"
                        id={`isDefaultShipping-${address.id}`}
                        checked={!!address.is_default_shipping}
                        disabled={!!address.is_default_shipping}
                        onChange={() => {
                          if (!address.is_default_shipping) {
                            setSetDefaultAddress(address.id)
                          }
                        }}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`isDefaultShipping-${address.id}`}
                        className="label-md text-secondary select-none"
                      >
                        {t("address_form.set_as_default_shipping")}
                      </label>
                    </>
                  )}
                </div>
              </Card>
            ))}
            {user.addresses.length < 6 && (
              <Button onClick={handleAdd}>{t("addresses.add_address_button")}</Button>
            )}
          </>
        )}
      </div>
      {showForm && (
        <Modal
          heading={
            defaultValues?.addressId
              ? `Edit adddress: ${defaultValues.addressName}`
              : t("addresses.add_address_button")
          }
          onClose={() => setShowForm(false)}
        >
          <AddressForm
            regions={regions}
            handleClose={() => setShowForm(false)}
            defaultValues={defaultValues || emptyDefaultAddressValues}
          />
        </Modal>
      )}
      {deleteAddress && (
        <Modal
          heading={t("addresses.confirm_action")}
          onClose={() => setDeleteAddress(null)}
        >
          <div className="px-4 flex flex-col gap-4">
            <p>{t("addresses.delete_address_confirmation")}</p>
            <div className="flex justify-end gap-4">
              <Button variant="tonal" onClick={() => setDeleteAddress(null)}>
                {t("addresses.cancel_button")}
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(deleteAddress)}
              >
                {t("addresses.delete_button")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {setDefaultAddress && (
        <Modal
          heading={t("addresses.set_default_confirm")}
          onClose={() => setSetDefaultAddress(null)}
        >
          <div className="px-4 flex flex-col gap-4">
            <p>{t("addresses.set_default_confirmation")}</p>
            <div className="flex justify-end gap-4">
              <Button variant="tonal" onClick={() => setSetDefaultAddress(null)}>
                {t("addresses.cancel_button")}
              </Button>
              <Button
                variant="filled"
                onClick={() => handleSetDefault(setDefaultAddress)}
              >
                {t("addresses.set_default_button")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
